import * as XLSX from 'xlsx';

// Export questions to Excel
export const exportQuestionsToExcel = (questions, filters = {}) => {
  try {
    // Prepare data for Excel
    const excelData = questions.map(question => ({
      'Question ID': question.id,
      'Title': question.title,
      'Category': question.category,
      'Status': question.status,
      'Tags': question.tags ? question.tags.join(', ') : '',
      'Asked By': question.asked_by,
      'Description': question.description || '',
      'Created Date': new Date(question.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const colWidths = [
      { wch: 35 }, // Question ID
      { wch: 50 }, // Title
      { wch: 20 }, // Category
      { wch: 15 }, // Status
      { wch: 25 }, // Tags
      { wch: 20 }, // Asked By
      { wch: 60 }, // Description
      { wch: 20 }  // Created Date
    ];
    worksheet['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions');

    // Add metadata sheet
    const metadataSheet = XLSX.utils.json_to_sheet([
      {
        'Export Details': 'Questions Export',
        'Generated On': new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        'Total Records': questions.length,
        'Filters Applied': Object.keys(filters).length > 0 ? 
          Object.entries(filters)
            .filter(([key, value]) => value)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ') : 'None'
      }
    ]);
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Export Info');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `PAL-NG_Questions_Export_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);

    return { success: true, filename, recordCount: questions.length };
  } catch (error) {
    console.error('Error exporting questions to Excel:', error);
    return { success: false, error: error.message };
  }
};

// Export feedback to Excel
export const exportFeedbackToExcel = (feedback, filters = {}) => {
  try {
    // Prepare data for Excel
    const excelData = feedback.map(item => ({
      'Feedback ID': item.id,
      'Title': item.title,
      'Category': item.category,
      'Feedback Type': item.feedback_type,
      'Source': item.source || '',
      'Author': item.author,
      'Description': item.description || '',
      'Upvotes': item.upvotes || 0,
      'Created Date': new Date(item.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const colWidths = [
      { wch: 35 }, // Feedback ID
      { wch: 50 }, // Title
      { wch: 20 }, // Category
      { wch: 20 }, // Feedback Type
      { wch: 15 }, // Source
      { wch: 20 }, // Author
      { wch: 60 }, // Description
      { wch: 10 }, // Upvotes
      { wch: 20 }  // Created Date
    ];
    worksheet['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Feedback');

    // Add metadata sheet
    const metadataSheet = XLSX.utils.json_to_sheet([
      {
        'Export Details': 'Feedback Export',
        'Generated On': new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        'Total Records': feedback.length,
        'Filters Applied': Object.keys(filters).length > 0 ? 
          Object.entries(filters)
            .filter(([key, value]) => value)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ') : 'None'
      }
    ]);
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Export Info');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `PAL-NG_Feedback_Export_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);

    return { success: true, filename, recordCount: feedback.length };
  } catch (error) {
    console.error('Error exporting feedback to Excel:', error);
    return { success: false, error: error.message };
  }
};

// Export learnings to Excel
export const exportLearningsToExcel = (learnings, filters = {}) => {
  try {
    // Prepare data for Excel
    const excelData = learnings.map(learning => ({
      'Learning ID': learning.id,
      'Title': learning.title,
      'Category': learning.category,
      'Tags': learning.tags ? learning.tags.join(', ') : '',
      'Shared By': learning.shared_by,
      'Content': learning.content || '',
      'Resources': learning.resources || '',
      'Created Date': new Date(learning.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const colWidths = [
      { wch: 35 }, // Learning ID
      { wch: 50 }, // Title
      { wch: 20 }, // Category
      { wch: 25 }, // Tags
      { wch: 20 }, // Shared By
      { wch: 60 }, // Content
      { wch: 40 }, // Resources
      { wch: 20 }  // Created Date
    ];
    worksheet['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Learnings');

    // Add metadata sheet
    const metadataSheet = XLSX.utils.json_to_sheet([
      {
        'Export Details': 'Learnings Export',
        'Generated On': new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        'Total Records': learnings.length,
        'Filters Applied': Object.keys(filters).length > 0 ? 
          Object.entries(filters)
            .filter(([key, value]) => value)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ') : 'None'
      }
    ]);
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Export Info');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `PAL-NG_Learnings_Export_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);

    return { success: true, filename, recordCount: learnings.length };
  } catch (error) {
    console.error('Error exporting learnings to Excel:', error);
    return { success: false, error: error.message };
  }
};

// Export combined data to Excel (all questions, learnings, and feedback)
export const exportAllDataToExcel = (questions, learnings, feedback) => {
  try {
    const workbook = XLSX.utils.book_new();

    // Questions sheet
    if (questions && questions.length > 0) {
      const questionsData = questions.map(question => ({
        'ID': question.id,
        'Title': question.title,
        'Category': question.category,
        'Status': question.status,
        'Tags': question.tags ? question.tags.join(', ') : '',
        'Asked By': question.asked_by,
        'Description': question.description || '',
        'Created Date': new Date(question.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }));
      const questionsSheet = XLSX.utils.json_to_sheet(questionsData);
      questionsSheet['!cols'] = [
        { wch: 35 }, { wch: 50 }, { wch: 20 }, { wch: 15 }, 
        { wch: 25 }, { wch: 20 }, { wch: 60 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(workbook, questionsSheet, 'Questions');
    }

    // Learnings sheet
    if (learnings && learnings.length > 0) {
      const learningsData = learnings.map(learning => ({
        'ID': learning.id,
        'Title': learning.title,
        'Category': learning.category,
        'Tags': learning.tags ? learning.tags.join(', ') : '',
        'Shared By': learning.shared_by,
        'Content': learning.content || '',
        'Resources': learning.resources || '',
        'Created Date': new Date(learning.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }));
      const learningsSheet = XLSX.utils.json_to_sheet(learningsData);
      learningsSheet['!cols'] = [
        { wch: 35 }, { wch: 50 }, { wch: 20 }, { wch: 25 }, 
        { wch: 20 }, { wch: 60 }, { wch: 40 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(workbook, learningsSheet, 'Learnings');
    }

    // Feedback sheet
    if (feedback && feedback.length > 0) {
      const feedbackData = feedback.map(item => ({
        'ID': item.id,
        'Title': item.title,
        'Category': item.category,
        'Feedback Type': item.feedback_type,
        'Source': item.source || '',
        'Author': item.author,
        'Description': item.description || '',
        'Upvotes': item.upvotes || 0,
        'Created Date': new Date(item.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }));
      const feedbackSheet = XLSX.utils.json_to_sheet(feedbackData);
      feedbackSheet['!cols'] = [
        { wch: 35 }, { wch: 50 }, { wch: 20 }, { wch: 20 }, 
        { wch: 15 }, { wch: 20 }, { wch: 60 }, { wch: 10 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(workbook, feedbackSheet, 'Feedback');
    }

    // Summary sheet
    const summaryData = [
      {
        'Data Type': 'Questions',
        'Total Records': questions ? questions.length : 0,
        'Export Date': new Date().toLocaleDateString('en-US')
      },
      {
        'Data Type': 'Learnings',
        'Total Records': learnings ? learnings.length : 0,
        'Export Date': new Date().toLocaleDateString('en-US')
      },
      {
        'Data Type': 'Feedback',
        'Total Records': feedback ? feedback.length : 0,
        'Export Date': new Date().toLocaleDateString('en-US')
      }
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `PAL-NG_Complete_Export_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);

    const totalRecords = (questions?.length || 0) + (learnings?.length || 0) + (feedback?.length || 0);
    return { success: true, filename, recordCount: totalRecords };
  } catch (error) {
    console.error('Error exporting all data to Excel:', error);
    return { success: false, error: error.message };
  }
};